import { createPinia } from 'pinia'
import { Profile } from '~/models/Profile'
import { ProfileService } from '~/passporter-services/profile/service'
import { useProfileStore } from '~/store/profile'
import CookieService from '~/services/cookies'
import EventsService from '~/services/events'
import {
	PredefinedDialog,
	PredefinedDialogType,
	DialogController,
} from '~/components/passporterUi/Dialog/dialogController'
import { EditProfileDestinations, ProfileEdit } from '#components'
import { TranslationService } from '~/services/Translation'
import { ProfileDestination, ProfileDestinationType } from '~/models/ProfileDestination'
import FileRestrictions from '~/passporter-services/shared/file'
import MediaService from '~/passporter-services/media/service'
import { Destination2 } from '~/models/Destination'
import { DestinationService } from '~/passporter-services/destination/service'

export class ProfileActions {
	static pinia = createPinia()

	public static async getProfile(): Promise<Profile> {
		try {
			const cookie = CookieService.getAuthCookie()
			const userId = cookie?.auth?.id
			if (!userId) throw new Error('User id not found')

			const stored: Profile | undefined = this.profileStore().profile
			if (stored?.id === userId) return stored

			const profile = await ProfileService.getProfile(userId)
			this.profileStore().setProfile(profile)

			return profile
		} catch (e) {
			console.error(e)
			return {}
		}
	}

	public static async getSharedProfile(id: string): Promise<Profile> {
		try {
			const profile = await ProfileService.getProfile(id)
			return profile
		} catch (e) {
			console.error(e)
			return {}
		}
	}

	public static async updateProfile(profile: Profile): Promise<void> {
		const translation: TranslationService = TranslationService.getInstance()
		try {
			await ProfileService.updateProfile(profile.id!, profile)
			this.profileStore().setProfile(profile)
			this.profileStore().resetCountry()
			EventsService.emitSuccess(translation.translate('Profile_edit_update_profile_success'))
		} catch {
			EventsService.emitError(translation.translate('Profile_edit_update_profile_error'))
		}
	}

	public static createEditDialog(profileProp: Profile) {
		const editDialogSetup = new PredefinedDialog(PredefinedDialogType.EMPTY, ProfileEdit)
			.props({ profile: profileProp })
			.title(TranslationService.getInstance().translate('Profile_Owner_Settings_edit_profile'))
			.size('s')
			.mobileType('fullScreen')
			.build()
		return new DialogController(editDialogSetup)
	}

	public static createDeleteDialog() {
		const translation: TranslationService = TranslationService.getInstance()
		const title: string = translation.translate('alert_removeDestination_heading')
		const body: string = translation.translate('alert_removeDestination_subheading')
		const deleteDialogSetup = new PredefinedDialog(PredefinedDialogType.DELETE_ALERT)
			.title(title)
			.bodyText(body)
			.size('s')
			.build()
		return new DialogController(deleteDialogSetup)
	}

	public static createAddDestinationDialog(type: ProfileDestinationType) {
		const translation: TranslationService = TranslationService.getInstance()
		const title: string = translation.translate('cta_button_adddestination')
		let colorPalette = 'primary'
		let placeholder = 'visitedDestinations_placeholder_question'

		if (type === 'wishlist') {
			placeholder = 'wishlist_placeholder_question'
			colorPalette = 'secondary'
		}

		const editDialogSetup = new PredefinedDialog(
			PredefinedDialogType.EMPTY,
			EditProfileDestinations
		)
			.title(title)
			.props({ colorPalette, placeholder: translation.translate(placeholder) })
			.size('s')
			.mobileType('fullScreen')
			.build()
		return new DialogController(editDialogSetup)
	}

	public static async getWishlistDestinations(profileId: string): Promise<ProfileDestination[]> {
		return ProfileService.getWishList(profileId)
	}

	public static async getVisitedDestinations(profileId: string): Promise<ProfileDestination[]> {
		return ProfileService.getVisitedDestinations(profileId)
	}

	public static async getCountry() {
		try {
			const profile = await this.getProfile()
			const stored: Destination2 | undefined = this.profileStore().country
			if (stored?.id) return stored
			const countryId = profile.country
			if (!countryId) return
			const country = await DestinationService.getDestination(countryId)
			this.profileStore().setCountry(country)
			return country
		} catch (error) {
			console.error(error)
		}
	}

	public static async uploadAvatar(file: File) {
		const translation: TranslationService = TranslationService.getInstance()

		if (this.exceedsSize(file.size)) {
			return EventsService.emitError(translation.translate('error_imageUpload'))
		}

		try {
			return await MediaService.uploadImage(file)
		} catch (e) {
			console.error(e)
			EventsService.emitError(translation.translate('Error_profile_picture_uploaded'))
		}
	}

	public static hasToCompleteProfile(profile: Profile): boolean {
		return ProfileService.hasToCompleteProfile(profile)
	}

	private static exceedsSize(size: number): boolean {
		const maxAllowedMB: number = 20
		return FileRestrictions.sizeIsGreaterThan(maxAllowedMB, size)
	}

	public static validateProfile = async (form: Profile) => {
		const errors = await ProfileService.validateProfile(form)
		if (!errors) return
		return this.translateErrors(errors)
	}

	private static translateErrors(
		errors: Partial<Record<keyof Profile, string>>
	): Partial<Record<keyof Profile, string>> {
		const translated: Partial<Record<keyof Profile, string>> = {
			name: this.translateNameError(errors.name!),
			country: this.translateCountryError(errors.country!),
			birthYear: this.translateBirthYearError(errors.birthYear!),
		}
		return translated
	}

	private static translateNameError(error: string): string {
		const translation: TranslationService = TranslationService.getInstance()
		const errors: Record<string, string> = {
			empty_value: translation.translate('input_errorMessage_fieldRequired') as string,
			invalid_format: translation.translate('name_description_minimumCharacters') as string,
		}
		return errors[error] || errors[0]!
	}

	private static translateCountryError(error: string): string {
		const translation: TranslationService = TranslationService.getInstance()
		const errors: Record<string, string> = {
			empty_value: translation.translate('input_errorMessage_fieldRequired') as string,
		}
		return errors[error] || errors[0]!
	}

	private static translateBirthYearError(error: string): string {
		const translation: TranslationService = TranslationService.getInstance()
		const errors: Record<string, string> = {
			empty_value: translation.translate('input_errorMessage_fieldRequired') as string,
			invalid_format: translation.translate('input_errorMessage_invalidYear') as string,
		}
		return errors[error] || errors[0]!
	}

	private static profileStore() {
		return useProfileStore()
	}
}
